/**
 * FinalisationDialog Component
 * Unified end-of-life dialog replacing separate Rollover and Terminate actions.
 * Shows per-investor financial summary (capital, interest, WHT, net value) and
 * lets user split each investor's net value into Contract Suspense (held for
 * future rollover) vs Paid Out (disbursement). Creates a pending_changes entry
 * for approval, or executes immediately for matured/maturing contracts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
} from '@mui/material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import ContractActionDialogBase from './ContractActionDialogBase';

// Format number with space thousand separator and period decimal
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Currency input: formatted when blurred, raw number when focused
function CurrencyInput({ value, onChange, label, helperText, disabled, sx, ...props }) {
  const [focused, setFocused] = useState(false);
  const showFormatted = !focused || disabled;
  const rawValue = (() => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : String(num);
  })();

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : rawValue}
      onChange={disabled ? undefined : (e) => onChange(e.target.value.replace(/\s/g, ''))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type={showFormatted ? 'text' : 'number'}
      inputProps={showFormatted ? {} : { step: 0.01 }}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

const FinalisationDialog = ({ open, onClose, contract, contractId: explicitContractId, onSubmitSuccess }) => {
  const contractId = explicitContractId || contract?.id;

  const [finalisationDate, setFinalisationDate] = useState('');
  const [reason, setReason] = useState('');
  const [investorRows, setInvestorRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Set default finalisation date when dialog opens
  // For Matured: use maturity date. For Active: use today.
  useEffect(() => {
    if (open && contract) {
      if (contract.status === 'Matured' && contract.maturity_date) {
        setFinalisationDate(format(new Date(contract.maturity_date), 'yyyy-MM-dd'));
      } else {
        setFinalisationDate(format(new Date(), 'yyyy-MM-dd'));
      }
      setReason('');
      setInvestorRows([]);
      setError(null);
    }
  }, [open, contract]);

  // Fetch financial summary when date changes
  const fetchSummary = useCallback(async (date) => {
    if (!contractId || !date) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet(`/contracts/${contractId}/finalisation-summary?finalisation_date=${date}`);
      const rows = (result.data || []).map((inv) => ({
        investor_id: inv.investor_id,
        investor_name: inv.investor_name,
        original_capital: parseFloat(inv.original_capital) || 0,
        accrued_interest: parseFloat(inv.accrued_interest) || 0,
        wht_rate: parseFloat(inv.wht_rate) || 0,
        wht_gross_up: inv.wht_gross_up || false,
        wht_amount: parseFloat(inv.wht_amount) || 0,
        net_value: parseFloat(inv.net_value) || 0,
        reserve_amount: parseFloat(inv.net_value) || 0, // default: all to suspense
        payout_amount: 0,
      }));
      setInvestorRows(rows);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load financial summary');
      setInvestorRows([]);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (open && finalisationDate) {
      fetchSummary(finalisationDate);
    }
  }, [open, finalisationDate, fetchSummary]);

  // Handle suspense amount change for an investor
  const handleSuspenseChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      row.reserve_amount = Math.max(0, Math.min(num, row.net_value));
      row.payout_amount = Math.max(0, row.net_value - row.reserve_amount);
      updated[index] = row;
      return updated;
    });
  };

  // Handle payout amount change for an investor
  const handlePayoutChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      row.payout_amount = Math.max(0, Math.min(num, row.net_value));
      row.reserve_amount = Math.max(0, row.net_value - row.payout_amount);
      updated[index] = row;
      return updated;
    });
  };

  // Quick-action: all to suspense
  const setAllSuspense = () => {
    setInvestorRows((prev) =>
      prev.map((row) => ({ ...row, reserve_amount: row.net_value, payout_amount: 0 }))
    );
  };

  // Quick-action: all paid out
  const setAllPayout = () => {
    setInvestorRows((prev) =>
      prev.map((row) => ({ ...row, reserve_amount: 0, payout_amount: row.net_value }))
    );
  };

  // Validation
  const allocationValid = investorRows.length > 0 && investorRows.every((row) => {
    const sum = row.reserve_amount + row.payout_amount;
    return Math.abs(sum - row.net_value) <= 0.01;
  });
  const isValid = !!finalisationDate && allocationValid && !!reason;

  // Totals
  const totalCapital = investorRows.reduce((s, r) => s + r.original_capital, 0);
  const totalInterest = investorRows.reduce((s, r) => s + r.accrued_interest, 0);
  const totalWht = investorRows.reduce((s, r) => s + r.wht_amount, 0);
  const totalNetValue = investorRows.reduce((s, r) => s + r.net_value, 0);
  const totalSuspense = investorRows.reduce((s, r) => s + r.reserve_amount, 0);
  const totalPayout = investorRows.reduce((s, r) => s + r.payout_amount, 0);

  // Submit
  const handleSubmit = async ({ proof_document_ids }) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        finalisation_date: finalisationDate,
        investor_allocations: investorRows.map((row) => ({
          investor_id: row.investor_id,
          original_capital: row.original_capital,
          accrued_interest: row.accrued_interest,
          wht_amount: row.wht_amount,
          wht_rate: row.wht_rate,
          wht_gross_up: row.wht_gross_up,
          net_value: row.net_value,
          reserve_amount: row.reserve_amount,
          payout_amount: row.payout_amount,
        })),
        reason,
        proof_document_ids,
      };
      const response = await apiPost(`/contracts/${contractId}/finalise`, payload);
      onClose();
      if (onSubmitSuccess) onSubmitSuccess(response.data?.status === 'executed' ? 'executed' : 'pending');
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to submit finalisation');
    } finally {
      setSubmitting(false);
    }
  };

  const currencyCode = contract?.currency_code || 'ZAR';

  return (
    <ContractActionDialogBase
      open={open}
      onClose={onClose}
      contract={contract}
      workflowCode="contract_finalise"
      title="Contract Finalisation"
      description={`Finalise this contract by allocating each investor's net value to contract suspense (held for future rollover) or payout (disbursement). ${contract?.status === 'Matured' ? 'This will be executed immediately.' : 'This will be submitted for approval.'}`}
      submitLabel={contract?.status === 'Matured' ? 'Finalise Contract' : 'Submit for Approval'}
      submitColor="primary"
      isValid={isValid}
      submitting={submitting}
      error={error}
      onClearError={() => setError(null)}
      onSubmit={handleSubmit}
      reason={reason}
      onReasonChange={setReason}
      maxWidth="lg"
    >
      {/* Finalisation Date */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            label="Finalisation Date"
            type="date"
            fullWidth
            value={finalisationDate}
            onChange={(e) => setFinalisationDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd') }}
            helperText="Max 30 days from today"
          />
        </Grid>
      </Grid>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">Loading financial summary...</Typography>
        </Box>
      )}

      {/* Financial Summary */}
      {!loading && investorRows.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>Financial Summary</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Outstanding Capital</Typography>
                <Typography variant="body2" fontWeight="medium">{currencyCode} {formatNumber(totalCapital)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Accrued Interest</Typography>
                <Typography variant="body2" fontWeight="medium">{currencyCode} {formatNumber(totalInterest)}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">WHT</Typography>
                <Typography variant="body2" fontWeight="medium" color="error.main">({currencyCode} {formatNumber(totalWht)})</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="textSecondary">Total Net Value</Typography>
                <Typography variant="body2" fontWeight="bold">{currencyCode} {formatNumber(totalNetValue)}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Quick actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Per-Investor Allocation</Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={setAllSuspense}>All to Suspense</Button>
              <Button onClick={setAllPayout}>All to Payout</Button>
            </ButtonGroup>
          </Box>

          {/* Per-investor allocation table */}
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Investor</TableCell>
                  <TableCell align="right">Capital</TableCell>
                  <TableCell align="right">Interest</TableCell>
                  <TableCell align="right">WHT</TableCell>
                  <TableCell align="right">Net Value</TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}>Contract Suspense</TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}>Payout</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {investorRows.map((row, idx) => (
                  <TableRow key={row.investor_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{row.investor_name}</Typography>
                      {row.wht_rate > 0 && (
                        <Typography variant="caption" color="textSecondary">
                          WHT: {(row.wht_rate * 100).toFixed(2)}%{row.wht_gross_up ? ' (gross-up)' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatWithSpaces(row.original_capital)}</TableCell>
                    <TableCell align="right">{formatWithSpaces(row.accrued_interest)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      {row.wht_amount > 0 ? `(${formatWithSpaces(row.wht_amount)})` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">{formatWithSpaces(row.net_value)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <CurrencyInput
                        size="small"
                        value={row.reserve_amount}
                        onChange={(v) => handleSuspenseChange(idx, v)}
                        sx={{ '& input': { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <CurrencyInput
                        size="small"
                        value={row.payout_amount}
                        onChange={(v) => handlePayoutChange(idx, v)}
                        sx={{ '& input': { textAlign: 'right' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><Typography variant="body2" fontWeight="bold">Total</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalCapital)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalInterest)}</Typography></TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}><Typography variant="body2" fontWeight="bold">{totalWht > 0 ? `(${formatWithSpaces(totalWht)})` : '-'}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalNetValue)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="primary">{formatWithSpaces(totalSuspense)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="warning.main">{formatWithSpaces(totalPayout)}</Typography></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary chips */}
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 3, justifyContent: 'flex-end', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="textSecondary">Contract Suspense</Typography>
              <Typography variant="body2" fontWeight="bold" color="primary">
                {currencyCode} {formatWithSpaces(totalSuspense)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">Total Payout</Typography>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                {currencyCode} {formatWithSpaces(totalPayout)}
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {/* No investors */}
      {!loading && investorRows.length === 0 && finalisationDate && !error && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          No investor data available. Please check the finalisation date.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />
    </ContractActionDialogBase>
  );
};

export default FinalisationDialog;
